/*    
<summary>
  This functional component "AddCmsDetailsForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import React, { useEffect, useState } from "react";
import { Form, Formik, Field } from "formik";
import { FormattedMessage } from "react-intl";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import { observer } from "mobx-react";
import LoaderButton from "../../../../shared-components/button/loader-button";
import Label from "../../../../shared-components/label/label";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import { addCmsDetailsValidationSchema } from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormikFormCheckBox from "../../../../shared-components/FormikFormHandlers/FormikFormCheckBox";
import FormikFormReactQuill from "../../../../shared-components/FormikFormHandlers/FormikFormReactQuill";
import { toHalfWidth } from "../../../../helpers/validate-helper";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  initialValues: any;
}

const AddCmsDetailsForm = (props: IProps) => {
  return (
    <>
      <Modal show={true} size="lg" centered>
        <Modal.Header>
          <Modal.Title>
            <FormattedMessage id="store_cms_details" />
            <button
              onClick={props.onModalClosed}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            ...props.initialValues
          }}
          enableReinitialize
          validationSchema={addCmsDetailsValidationSchema}
          validateOnBlur={false}
          onSubmit={async (values) => {
            const data = {
              ...values,
              AccessInformation: toHalfWidth(values.AccessInformation)
            };
            props.submitHandler(data);
          }}
        >
          {({ setFieldValue, values, errors, touched }) => (
            <Form className="user-form">
              <Modal.Body>
                {/* Row 1: Shift Name and Shift Short Name */}
                <div className="row">
                  <div className="col-12  form-group">
                    <Label
                      className="label-style"
                      label="store_cms_details"
                    />
                    <Field
                      className="form-control"
                      name="StoreDetails"
                      type="text"                      
                      placeholder="enter_store_cms_details"
                      as={FormikFormReactQuill}
                      rows={3}
                    />
                  </div>
                  <div className="col-12 form-group">
                    <Label
                      className="label-style"
                      label="map_url"
                    />
                    <Field
                      className="form-control"
                      name="StoreMapURL"
                      type="text"
                      placeholder="enter_store_map_url"
                      as={FormikFormTextArea}
                      rows={3}
                    />
                  </div>
                  <div className="col-12 form-group">
                    <Label
                      className="label-style"
                      label="access_directions"
                    />
                    <Field
                      className="form-control"
                      name="AccessInformation"
                      type="text"
                      validcharacter={[
                        "~",
                        "`",
                        "!",
                        "@",
                        "#",
                        "$",
                        "%",
                        "^",
                        "&",
                        "*",
                        "(",
                        ")",
                        "_",
                        "-",
                        "+",
                        "=",
                        "{",
                        "[",
                        "}",
                        "]",
                        "|",
                        "\\",
                        ":",
                        ";",
                        "'",
                        '"',
                        "<",
                        ",",
                        ">",
                        ".",
                        "?",
                        "/",
                      ]}
                      placeholder="enter_access_directions"
                      as={FormikFormInput}
                      rows={3}
                    />
                  </div>
                  <div className="col-12 form-group">
                    <div className="d-flex justify-content-between align-items-center">
                      <Label
                        className="label-style"
                        label="message"
                      />
                      <div className="form-check">
                        <label htmlFor="PhoneMessageVisible">
                          <Field
                            className="form-check-input mt-0"
                            name="PhoneMessageVisible"
                            id="PhoneMessageVisible"
                            type="checkbox"
                            as={FormikFormCheckBox}
                          />
                          <FormattedMessage id="visible" />
                        </label>
                      </div>
                    </div>
                    <Field
                      className="form-control"
                      name="StorePhoneMessage"
                      type="text"
                      placeholder="enter_message"
                      as={FormikFormTextArea}
                      rows={3}
                    />
                  </div>
                  
                </div>
              </Modal.Body>

              {/* Footer Buttons */}
              <div className="modal-footer center-content">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={props.onModalClosed}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  <FormattedMessage id="close" />
                </Button>
                <LoaderButton
                  type="submit"
                  iconName="bi-check-lg"
                  text={props.id > -1 ? "button_update" : "button_save"}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default observer(AddCmsDetailsForm);
